import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  FullScreenModal,
  FullScreenModalBody,
  FullScreenModalFooter,
  FullScreenModalHeader,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";
import { Color } from "@transfr-inc/dashboard-components/utils";

import { useContainer } from "@contexts/container-context";
import {
  useAiCurriculumEnabled,
  useAiCurriculumSession,
  useAiCurriculumSettings,
} from "@queries/ai-curriculum";
import { AILayoutBackground } from "@src/layouts/ai";
import { useAiCurriculumUIStore } from "@src/stores/zustand/ai-curriculum-ui.store";

import {
  AICA_SESSION_PARAM,
  CRITERIA_CONFLICT_CODES,
  ENTRY_TYPES,
  FAILED_STAGES,
  SESSION_STATUS,
  STANDARDS_MODE,
  STEPS,
  SYLLABUS_UPLOAD_ERRORS,
} from "./ai-curriculum.constants";
import {
  buildQuickStartPayload,
  buildStandardsPayload,
  buildSyllabusUploadPayload,
  criteriaFromWire,
  defaultCriteria,
  hasRequiredCriteria,
  quickStartCriteriaFromSuggestions,
  syllabusCriteriaFromAnalyzer,
  validateQuickStartCriteria,
} from "./ai-curriculum.criteria";
import {
  EMPTY_STATE_SELECTION,
  deriveStepFromStatus,
  stateSelectionFromContext,
} from "./ai-curriculum.rehydrate";
import { AiModalFooterNote, AiStepCounter } from "./components";
import {
  AiBuildCompleteStep,
  AiEntryPointStep,
  AiErrorStep,
  AiFormStep,
  AiLoadingStep,
  AiOverridesModal,
  AiRecommendationsStep,
  AiStateSelectStep,
  AiSyllabusUploadStep,
  castTunableValue,
  isAllWithinSubjectOption,
  validateOverrideSettings,
  validateSyllabusFile,
} from "./steps";
import { useAiCurriculumDraft } from "./use-ai-curriculum-draft";

import "./ai-curriculum.modal.scss";

// 401 = no access to the target org; the specific message avoids
// suggesting a retry for a non-transient condition.
const ORG_ACCESS_ERROR =
  "You don't have access to this classroom's organization. Contact your administrator if you believe this is a mistake.";

const PREVENT_CLOSE_STEPS = new Set([
  STEPS.ANALYZING,
  STEPS.LOADING_STANDARDS,
  STEPS.RECOMMENDING,
  STEPS.BUILDING,
]);

// Progress counter shown in the footer once the user is inside the flow, keyed
// by entry path. State Standards and Upload have an extra input page (state
// select / file upload) before the criteria form, so they show 3 dots; Quick
// Start shows 2. Loading/terminal steps inherit the dot of the phase they
// belong to. Entry types / steps not listed (entry point, error) show the
// branding note instead.
const STEP_COUNTERS = {
  [ENTRY_TYPES.STATE_STANDARDS]: {
    total: 3,
    index: {
      [STEPS.STATE_SELECT]: 0,
      [STEPS.LOADING_STANDARDS]: 0,
      [STEPS.FORM]: 1,
      [STEPS.RECOMMENDING]: 1,
      [STEPS.RECOMMENDATIONS]: 2,
      [STEPS.BUILDING]: 2,
      [STEPS.COMPLETE]: 2,
    },
  },
  [ENTRY_TYPES.SYLLABUS_UPLOAD]: {
    total: 3,
    index: {
      [STEPS.SYLLABUS_UPLOAD]: 0,
      [STEPS.ANALYZING]: 0,
      [STEPS.FORM]: 1,
      [STEPS.RECOMMENDING]: 1,
      [STEPS.RECOMMENDATIONS]: 2,
      [STEPS.BUILDING]: 2,
      [STEPS.COMPLETE]: 2,
    },
  },
  [ENTRY_TYPES.QUICK_START]: {
    total: 2,
    index: {
      [STEPS.FORM]: 0,
      [STEPS.RECOMMENDING]: 0,
      [STEPS.RECOMMENDATIONS]: 1,
      [STEPS.BUILDING]: 1,
      [STEPS.COMPLETE]: 1,
    },
  },
};

export const AiCurriculumModal = ({
  open,
  onClose,
  orgCode,
  classroomId,
  onBuildComplete,
}) => {
  const { aiCurriculumService } = useContainer();
  const history = useHistory();
  const location = useLocation();
  const clearDraft = useAiCurriculumUIStore((s) => s.clearDraft);

  // Which session to resume, if any.
  const [restoredSessionId] = useState(() =>
    new URLSearchParams(location.search).get(AICA_SESSION_PARAM)
  );

  const [step, setStep] = useState(() =>
    restoredSessionId ? STEPS.REHYDRATING : STEPS.ENTRY_POINT
  );
  const [session, setSession] = useState(null);
  const [standardsContext, setStandardsContext] = useState(null);
  const [stateSelection, setStateSelection] = useState(EMPTY_STATE_SELECTION);
  const [criteria, setCriteria] = useState(defaultCriteria);
  const [criteriaErrors, setCriteriaErrors] = useState({});
  const [analyzerSummary, setAnalyzerSummary] = useState(null);
  const [syllabusUploadFailure, setSyllabusUploadFailure] = useState(null);
  const [syllabusFile, setSyllabusFile] = useState(null);
  const [syllabusFileError, setSyllabusFileError] = useState(null);
  const [uploadingSyllabus, setUploadingSyllabus] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [converting, setConverting] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [criteriaSubmitted, setCriteriaSubmitted] = useState(false);
  const [pollRunKey, setPollRunKey] = useState(0);
  const [standardsError, setStandardsError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [buildError, setBuildError] = useState(null);
  const [buildResult, setBuildResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [submittingStandards, setSubmittingStandards] = useState(false);
  const [submittingCriteria, setSubmittingCriteria] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const [overrideSettings, setOverrideSettings] = useState({});
  const [taskInstructions, setTaskInstructions] = useState("");
  const [overrideErrors, setOverrideErrors] = useState({});
  const [overridesOpen, setOverridesOpen] = useState(false);

  const { data: featureFlags } = useAiCurriculumEnabled();
  const settingsEditingEnabled = !!featureFlags?.settingsEditingEnabled;
  const { data: settingsCatalog, isLoading: settingsLoading } =
    useAiCurriculumSettings(settingsEditingEnabled);

  const reset = useCallback(() => {
    setStep(STEPS.ENTRY_POINT);
    setSession(null);
    setStandardsContext(null);
    setStateSelection(EMPTY_STATE_SELECTION);
    setCriteria(defaultCriteria());
    setCriteriaErrors({});
    setAnalyzerSummary(null);
    setSyllabusUploadFailure(null);
    setSyllabusFile(null);
    setSyllabusFileError(null);
    setUploadingSyllabus(false);
    setUploadProgress(0);
    setConverting(false);
    setRecommendations(null);
    setCriteriaSubmitted(false);
    setPollRunKey((k) => k + 1);
    setStandardsError(null);
    setSubmitError(null);
    setBuildError(null);
    setBuildResult(null);
    setErrorMessage(null);
    setSubmittingStandards(false);
    setSubmittingCriteria(false);
    setDownloading(false);
    setDownloadError(null);
    setOverrideSettings({});
    setTaskInstructions("");
    setOverrideErrors({});
    setOverridesOpen(false);
    clearDraft(orgCode, classroomId);
  }, [orgCode, classroomId, clearDraft]);

  const handleSettingChange = useCallback((key, value) => {
    setOverrideSettings((prev) => {
      if (value === undefined) {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: value };
    });
    // Clear any stale validation error for this field as the user edits it.
    setOverrideErrors((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleClose = useCallback(() => {
    if (PREVENT_CLOSE_STEPS.has(step)) return;
    reset();
    onClose?.();
  }, [step, reset, onClose]);

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setStep(STEPS.ERROR);
  }, []);

  // Refresh persistence (ENG-23196). Gated on `open` so a draft left behind by
  // in-app navigation doesn't spend a status request until the wizard reopens.
  const rehydrating = step === STEPS.REHYDRATING && open;

  const { data: sessionDetail, isError: sessionDetailFailed } =
    useAiCurriculumSession(
      orgCode,
      rehydrating ? restoredSessionId : undefined
    );

  const abandonRestore = useCallback(() => {
    reset();
    onClose?.();
  }, [reset, onClose]);

  // The session is the source of truth for everything it holds; the draft
  // supplies only the local edits that never reached it. The step comes from
  // the session's status because work continues while the page is gone — one
  // left `recommending` may have since recommended or failed.
  useEffect(() => {
    if (!rehydrating) return;
    if (sessionDetailFailed) {
      abandonRestore();
      return;
    }
    if (!sessionDetail) return;

    const resumed = deriveStepFromStatus(
      sessionDetail,
      sessionDetail.entryType
    );
    if (!resumed) {
      abandonRestore();
      return;
    }

    const draft = useAiCurriculumUIStore
      .getState()
      .getDraft(orgCode, classroomId);
    // A draft from a different session is stale — the URL wins.
    const localEdits =
      draft?.sessionId === restoredSessionId ? draft : undefined;
    const submittedCriteria = criteriaFromWire(sessionDetail.criteria);

    setSession({
      id: restoredSessionId,
      entryType: sessionDetail.entryType,
    });
    setStandardsContext(sessionDetail.standardsContext ?? null);
    setStateSelection(
      localEdits?.stateSelection ??
        stateSelectionFromContext(sessionDetail.standardsContext) ??
        EMPTY_STATE_SELECTION
    );
    setCriteria(localEdits?.criteria ?? submittedCriteria ?? defaultCriteria());
    setCriteriaSubmitted(!!submittedCriteria);
    setAnalyzerSummary(sessionDetail.analyzerSummary ?? null);
    setOverrideSettings(localEdits?.overrideSettings ?? {});
    setTaskInstructions(localEdits?.taskInstructions ?? "");

    if (resumed.syllabusUploadFailure) {
      setSyllabusUploadFailure(resumed.syllabusUploadFailure);
    }
    if (resumed.submitError) setSubmitError(resumed.submitError);
    setStep(resumed.step);
  }, [
    rehydrating,
    sessionDetail,
    sessionDetailFailed,
    restoredSessionId,
    orgCode,
    classroomId,
    abandonRestore,
  ]);

  // Memoized: the draft's identity re-arms the debounce, so a fresh object
  // each render would push the save out forever.
  const draft = useMemo(
    () => ({
      sessionId: session?.id,
      criteria,
      stateSelection,
      overrideSettings,
      taskInstructions,
    }),
    [session, criteria, stateSelection, overrideSettings, taskInstructions]
  );

  useAiCurriculumDraft(
    orgCode,
    classroomId,
    draft,
    open && !rehydrating && !!session?.id
  );

  // The session id in the URL is what remounts the wizard after a refresh.
  // Dropped on COMPLETE — a built session has nothing to resume.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const current = params.get(AICA_SESSION_PARAM);
    const desired =
      open && step !== STEPS.COMPLETE ? session?.id ?? null : null;
    if (current === desired) return;

    if (desired) params.set(AICA_SESSION_PARAM, desired);
    else params.delete(AICA_SESSION_PARAM);

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
      hash: location.hash,
      state: location.state,
    });
  }, [open, step, session?.id, location, history]);

  const handleEntrySelected = useCallback(({ entryType, sessionId }) => {
    setSession({ id: sessionId, entryType });
    switch (entryType) {
      case ENTRY_TYPES.STATE_STANDARDS: {
        setStep(STEPS.STATE_SELECT);

        break;
      }
      case ENTRY_TYPES.SYLLABUS_UPLOAD: {
        setStep(STEPS.SYLLABUS_UPLOAD);

        break;
      }
      case ENTRY_TYPES.QUICK_START: {
        setStep(STEPS.FORM);

        break;
      }
      // No default
    }
  }, []);

  const handleStandardsSubmit = useCallback(async () => {
    const selectedState = stateSelection.state;
    const selectedSubject = stateSelection.subject;
    const selectedSourceName = stateSelection.sourceName;
    if (!session?.id || !selectedState || !selectedSubject) return;

    // "All within subject" maps to no sourceName drill-down. A concrete
    // sourceName option becomes the wire field.
    const sourceName =
      selectedSourceName && !isAllWithinSubjectOption(selectedSourceName)
        ? selectedSourceName.id
        : null;

    setSubmittingStandards(true);
    setStandardsError(null);
    try {
      await aiCurriculumService.loadStateStandards(orgCode, session.id, {
        mode: STANDARDS_MODE.SUBJECT,
        state: selectedState.id,
        subject: selectedSubject.id,
        ...(sourceName ? { sourceName } : {}),
      });
      setStandardsContext({
        state: selectedState.id,
        subject: selectedSubject.id,
        sourceName,
      });
      setPollRunKey((k) => k + 1);
      setStep(STEPS.LOADING_STANDARDS);
    } catch (error) {
      setStandardsError(
        error?.status === 401
          ? ORG_ACCESS_ERROR
          : "We couldn't start loading state standards. Please try again."
      );
    } finally {
      setSubmittingStandards(false);
    }
  }, [orgCode, session, stateSelection, aiCurriculumService]);

  const handleStandardsLoaded = useCallback(() => {
    setStep(STEPS.FORM);
  }, []);

  const handleStandardsLoadFailed = useCallback((message) => {
    setStandardsError(message || "We couldn't load state standards.");
    setStep(STEPS.STATE_SELECT);
  }, []);

  // Validate at pick time so obvious problems (type/size) surface before any
  // bytes are sent; a valid pick replaces the previous file.
  const handleSyllabusFilePicked = useCallback((file) => {
    const validationError = validateSyllabusFile(file);
    setSyllabusFileError(validationError);
    setSyllabusFile(validationError ? null : file);
  }, []);

  // Footer "Upload & Analyze" CTA. On success the analyzer runs server-side:
  // clear any previous failure (re-upload re-runs analysis) and show the
  // processing state, polling until analyzed/failed.
  const handleSyllabusUpload = useCallback(async () => {
    if (!session?.id || !syllabusFile || uploadingSyllabus) return;
    setUploadingSyllabus(true);
    setSyllabusFileError(null);
    setUploadProgress(0);
    try {
      await aiCurriculumService.uploadSyllabus(
        orgCode,
        session.id,
        syllabusFile,
        {
          onUploadProgress: (event) => {
            if (event.total) {
              setUploadProgress(Math.round((event.loaded / event.total) * 100));
            }
          },
        }
      );
      setSyllabusUploadFailure(null);
      setPollRunKey((k) => k + 1);
      setStep(STEPS.ANALYZING);
    } catch (uploadError) {
      // Top of the async chain — nothing above to rethrow to. Log the raw
      // error for debugging, then surface the friendliest message we have:
      // backend 400s carry a specific validation message (type/size).
      console.error("Syllabus upload failed", uploadError);
      if (uploadError?.status === 401) {
        setSyllabusFileError(ORG_ACCESS_ERROR);
        return;
      }
      const detail =
        typeof uploadError?.data?.detail === "string"
          ? uploadError.data.detail
          : null;
      setSyllabusFileError(detail ?? SYLLABUS_UPLOAD_ERRORS.UPLOAD_FAILED);
    } finally {
      setUploadingSyllabus(false);
    }
  }, [orgCode, session, syllabusFile, uploadingSyllabus, aiCurriculumService]);

  // Analyzer finished — prefill the form from its suggestions (ENG-23218) and
  // advance. Every prefilled value stays editable; Match Flexibility defaults
  // to Flexible on this path.
  const handleAnalyzed = useCallback((statusResponse) => {
    const summary = statusResponse?.analyzerSummary ?? null;
    setAnalyzerSummary(summary);
    setCriteria((prev) => ({
      ...prev,
      ...syllabusCriteriaFromAnalyzer(summary),
    }));
    setStep(STEPS.FORM);
  }, []);

  // Analysis failed (or timed out). No silent fallback (ENG-23216): return to
  // the upload step in recovery mode, which offers re-upload or an explicit
  // conversion to Quick Start.
  const handleAnalysisFailed = useCallback((message) => {
    setSyllabusUploadFailure(message || "Syllabus analysis failed.");
    setStep(STEPS.SYLLABUS_UPLOAD);
  }, []);

  // Routes a FAILED poll response to its recovery step by `failedStage`
  // (ENG-23395): upload/analysis failures recover on the upload step,
  // recommendation failures (and timeouts, which carry no response) retry
  // from the criteria form — the session accepts a criteria re-submission
  // from a recommender-stage failure.
  const handleRecommendingFailed = useCallback(
    (message, statusResponse) => {
      const stage = statusResponse?.failedStage;
      if (stage === FAILED_STAGES.UPLOAD || stage === FAILED_STAGES.ANALYSIS) {
        handleAnalysisFailed(message);
        return;
      }
      setSubmitError(
        `${
          message || "We couldn't generate recommendations."
        } Your answers are saved — you can retry or adjust them first.`
      );
      setStep(STEPS.FORM);
    },
    [handleAnalysisFailed]
  );

  // The explicit escape hatch: swap the wizard onto a fresh Quick Start
  // session, carrying over any analyzer suggestions without clobbering values
  // the user already picked.
  const handleConvertToQuickStart = useCallback(async () => {
    if (!session?.id || converting) return;
    setConverting(true);
    try {
      const response = await aiCurriculumService.convertToQuickStart(
        orgCode,
        session.id
      );
      setSession({ id: response.sessionId, entryType: response.entryType });
      setCriteria((prev) =>
        quickStartCriteriaFromSuggestions(prev, response.suggestedCriteria)
      );
      setAnalyzerSummary(null);
      setSyllabusUploadFailure(null);
      setCriteriaErrors({});
      // Fresh session — no criteria submitted against it yet.
      setCriteriaSubmitted(false);
      setStep(STEPS.FORM);
    } catch (error) {
      // Top of the async chain — rethrowing would only produce an unhandled
      // rejection. Log the raw error, then keep the user on the upload step's
      // recovery view (re-upload / convert stay available) with an inline
      // failure message instead of the terminal error step.
      console.error("Convert to Quick Start failed", error);
      setSyllabusUploadFailure(
        error?.status === 401
          ? ORG_ACCESS_ERROR
          : "We couldn't switch this session to Quick Start. Please try again."
      );
      setStep(STEPS.SYLLABUS_UPLOAD);
    } finally {
      setConverting(false);
    }
  }, [orgCode, session, converting, aiCurriculumService]);

  const handleCriteriaChange = useCallback((patch) => {
    setCriteria((prev) => ({ ...prev, ...patch }));
    // Clear inline errors for the fields the user just edited. Changing the
    // industry area away from "Other" also clears the conditional specifics
    // error.
    setCriteriaErrors((prev) => {
      const keys = Object.keys(patch);
      const clearSpecifics =
        "industryArea" in patch && patch.industryArea?.id !== "other";
      if (
        !keys.some((k) => k in prev) &&
        !(clearSpecifics && prev.industrySpecifics)
      )
        return prev;
      const rest = { ...prev };
      for (const k of keys) delete rest[k];
      if (clearSpecifics) delete rest.industrySpecifics;
      return rest;
    });
  }, []);

  const handleCriteriaSubmit = useCallback(async () => {
    if (!session?.id) return;

    const isQuickStart = session.entryType === ENTRY_TYPES.QUICK_START;
    const isUpload = session.entryType === ENTRY_TYPES.SYLLABUS_UPLOAD;

    if (isQuickStart || isUpload) {
      // Both paths collect an industry area, sharing the conditional
      // "specifics required for Other" rule.
      const errors = validateQuickStartCriteria(criteria);
      if (Object.keys(errors).length > 0) {
        setCriteriaErrors(errors);
        return;
      }
    } else if (
      !criteria.programType ||
      !criteria.usageFrequency ||
      !criteria.matchFlexibility
    ) {
      return;
    }

    if (settingsEditingEnabled) {
      const clientErrors = validateOverrideSettings(
        overrideSettings,
        settingsCatalog?.settings
      );
      if (Object.keys(clientErrors).length > 0) {
        setOverrideErrors(clientErrors);
        setOverridesOpen(true);
        return;
      }
    }

    setSubmittingCriteria(true);
    setSubmitError(null);
    setCriteriaErrors({});
    setOverrideErrors({});
    try {
      const trimmedInstructions = taskInstructions.trim();
      const typeByKey = new Map(
        (settingsCatalog?.settings ?? []).map((s) => [s.key, s.type])
      );
      const settings = Object.fromEntries(
        Object.entries(overrideSettings).map(([key, raw]) => [
          key,
          castTunableValue(typeByKey.get(key), raw),
        ])
      );
      const hasOverrides =
        settingsEditingEnabled &&
        (Object.keys(settings).length > 0 || trimmedInstructions);
      const overrides = hasOverrides
        ? {
            settings,
            ...(trimmedInstructions
              ? { taskInstructions: trimmedInstructions }
              : {}),
          }
        : undefined;

      let payload;
      if (isQuickStart) {
        payload = buildQuickStartPayload(criteria, overrides);
      } else if (isUpload) {
        payload = buildSyllabusUploadPayload(criteria, overrides);
      } else {
        payload = buildStandardsPayload(criteria, standardsContext, overrides);
      }

      await aiCurriculumService.submitCriteria(orgCode, session.id, payload);
      // A re-submission (back-navigation or retry) starts a fresh recommender
      // run that resets keep/remove selections and the feedback rating
      // server-side — drop the stale local copy so the
      // recommendations step refetches.
      setRecommendations(null);
      setBuildError(null);
      setCriteriaSubmitted(true);
      setPollRunKey((k) => k + 1);
      setStep(STEPS.RECOMMENDING);
    } catch (error) {
      if (error?.status === 401) {
        setSubmitError(ORG_ACCESS_ERROR);
        setStep(STEPS.FORM);
        return;
      }
      const validationErrors = error?.data?.detail?.errors;
      if (error?.status === 422 && Array.isArray(validationErrors)) {
        setOverrideErrors(
          Object.fromEntries(validationErrors.map((e) => [e.key, e.message]))
        );
        setOverridesOpen(true);
        return;
      }
      // Strict upload-path conflicts (ENG-23216): analysis problems surface
      // explicitly rather than silently degrading to a manual form.
      const detail = error?.data?.detail;
      if (error?.status === 409 && detail?.code) {
        if (detail.code === CRITERIA_CONFLICT_CODES.SESSION_NOT_READY) {
          // Work is still in flight. If we already hold recommendations the
          // user navigated back while a run existed — rejoin the recommending
          // poll. Otherwise this is an upload session whose
          // analysis hasn't finished — return to the processing state.
          setPollRunKey((k) => k + 1);
          setStep(recommendations ? STEPS.RECOMMENDING : STEPS.ANALYZING);
          return;
        }
        if (
          detail.code === CRITERIA_CONFLICT_CODES.ANALYSIS_FAILED ||
          detail.code === CRITERIA_CONFLICT_CODES.ANALYSIS_EMPTY
        ) {
          // Back to the upload step in recovery mode: re-upload or convert.
          setSyllabusUploadFailure(detail.message);
          setStep(STEPS.SYLLABUS_UPLOAD);
          return;
        }
      }
      console.error("Criteria submit failed", error);
      setSubmitError("We couldn't submit your preferences. Please try again.");
      setStep(STEPS.FORM);
    } finally {
      setSubmittingCriteria(false);
    }
  }, [
    orgCode,
    session,
    criteria,
    standardsContext,
    recommendations,
    aiCurriculumService,
    settingsEditingEnabled,
    overrideSettings,
    taskInstructions,
    settingsCatalog,
  ]);

  const handleRecommendationsReady = useCallback(() => {
    setStep(STEPS.RECOMMENDATIONS);
  }, []);

  // Back-navigation. Form values are kept; each entry path
  // returns to its own input step (Quick Start's input step IS the entry
  // card, so it resets). The backend accepts re-submission — standards from
  // `standards_loaded`, criteria from `recommended` — so going back and
  // forward re-runs the affected phase.
  const handleFormBack = useCallback(() => {
    setSubmitError(null);
    switch (session?.entryType) {
      case ENTRY_TYPES.STATE_STANDARDS: {
        setStep(STEPS.STATE_SELECT);
        break;
      }
      case ENTRY_TYPES.SYLLABUS_UPLOAD: {
        setStep(STEPS.SYLLABUS_UPLOAD);
        break;
      }
      default: {
        reset();
      }
    }
  }, [session, reset]);

  // Back from the review screen to the criteria form. Submitting the form
  // again starts a fresh recommender run (selections and feedback reset);
  // there is no forward path that keeps the current run.
  const handleRecommendationsBack = useCallback(() => {
    setBuildError(null);
    setStep(STEPS.FORM);
  }, []);

  const handleBuildRequested = useCallback(async () => {
    if (!session?.id) return;
    setBuildError(null);
    setStep(STEPS.BUILDING);
    try {
      // Re-send the keep/remove selection before building. The review step
      // already persists each toggle (ENG-21163); this reconciles anything that
      // failed to write, since Build My Course honors removed_by_user
      // (ENG-23110). The backend merges removedByUser by id and ignores the
      // other round-tripped fields, and a no-op merge emits no events.
      await aiCurriculumService.updateRecommendationSelection(
        orgCode,
        session.id,
        {
          recommendations: recommendations ?? [],
        }
      );
      const result = await aiCurriculumService.buildCourse(
        orgCode,
        session.id,
        {
          classroomId,
        }
      );
      setBuildResult(result);
      setStep(STEPS.COMPLETE);
      clearDraft(orgCode, classroomId);
      onBuildComplete?.(result);
    } catch (error) {
      // A recommender run is still in flight (409 SESSION_NOT_READY) — the
      // review screen was showing a previous run's artifact. Rejoin the
      // generating state and refetch once the new run lands.
      if (
        error?.status === 409 &&
        error?.data?.detail?.code === CRITERIA_CONFLICT_CODES.SESSION_NOT_READY
      ) {
        setRecommendations(null);
        setPollRunKey((k) => k + 1);
        setStep(STEPS.RECOMMENDING);
        return;
      }
      // Any other failed build is retryable, not fatal: the session stays
      // in `recommended`, so we keep the review screen (selections intact)
      // and the footer CTA becomes "Retry Build" — another plain POST /course.
      console.error("Build course failed", error);
      const detail =
        typeof error?.data?.detail === "string" ? error.data.detail : null;
      setBuildError(
        error?.status === 401
          ? ORG_ACCESS_ERROR
          : detail ??
              "We couldn't build your course. Your selections are saved — you can retry."
      );
      setStep(STEPS.RECOMMENDATIONS);
    }
  }, [
    orgCode,
    session,
    classroomId,
    recommendations,
    aiCurriculumService,
    onBuildComplete,
    clearDraft,
  ]);

  const handleDownloadArtifacts = useCallback(async () => {
    if (!session?.id) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await aiCurriculumService.downloadArtifacts(orgCode, session.id);
    } catch (error) {
      // The HttpClient error interceptor rejects with error.response, so the
      // status is at the top level. Non-fatal: stay on the current step.
      // 403 = caller isn't an admin (or the org's flag is off).
      const message =
        error?.status === 403
          ? "You don't have permission to download session artifacts."
          : "We couldn't download the session artifacts. Please try again.";
      setDownloadError(message);
    } finally {
      setDownloading(false);
    }
  }, [orgCode, session, aiCurriculumService]);

  // Only state + subject are required to load standards; sourceName is an
  // optional drill-down ("All within subject" is a valid choice).
  const canSubmitStandards = !!stateSelection.state && !!stateSelection.subject;
  const canSubmitCriteria = hasRequiredCriteria(
    criteria,
    session?.entryType,
    standardsContext
  );
  const canBuild = useMemo(
    () => (recommendations ?? []).some((r) => !r.removedByUser),
    [recommendations]
  );

  const footer = (() => {
    switch (step) {
      case STEPS.SYLLABUS_UPLOAD: {
        return (
          <>
            <Button onClick={reset}>Back</Button>
            {syllabusUploadFailure && (
              <Button
                disabled={converting || uploadingSyllabus}
                loader={converting}
                onClick={handleConvertToQuickStart}
              >
                Continue with Quick Start
              </Button>
            )}
            <Button
              primary
              disabled={!syllabusFile || uploadingSyllabus}
              loader={uploadingSyllabus}
              onClick={handleSyllabusUpload}
            >
              {syllabusUploadFailure
                ? "Re-upload & Analyze"
                : "Upload & Analyze"}
            </Button>
          </>
        );
      }
      case STEPS.STATE_SELECT: {
        return (
          <>
            <Button disabled={submittingStandards} onClick={reset}>
              Back
            </Button>
            <Button
              primary
              disabled={!canSubmitStandards || submittingStandards}
              onClick={handleStandardsSubmit}
            >
              Continue
            </Button>
          </>
        );
      }
      case STEPS.FORM: {
        return (
          <>
            {!criteriaSubmitted && (
              <Button disabled={submittingCriteria} onClick={handleFormBack}>
                Back
              </Button>
            )}
            <Button
              primary
              disabled={!canSubmitCriteria || submittingCriteria}
              onClick={handleCriteriaSubmit}
            >
              Generate Recommendations
            </Button>
          </>
        );
      }
      case STEPS.RECOMMENDATIONS: {
        return (
          <>
            <Button onClick={handleRecommendationsBack}>Back</Button>
            <Button primary disabled={!canBuild} onClick={handleBuildRequested}>
              {buildError ? "Retry Build" : "Build My Course"}
            </Button>
          </>
        );
      }
      case STEPS.COMPLETE: {
        return (
          <Button primary onClick={handleClose}>
            Done
          </Button>
        );
      }
      case STEPS.ERROR: {
        return (
          <>
            <Button onClick={handleClose}>Close</Button>
            <Button primary onClick={reset}>
              Try Again
            </Button>
          </>
        );
      }
      default: {
        return null;
      }
    }
  })();

  // The artifacts download is an admin/eval tool that persists across every
  // step once a session exists (nothing to download before the entry card is
  // picked). Server-side admin + feature-flag checks are the real gate.
  const showDownload = !!session?.id && !rehydrating;

  const stepCounter = STEP_COUNTERS[session?.entryType];
  const stepIndex = stepCounter?.index[step];
  const showStepCounter = stepIndex !== undefined;

  return (
    <>
      <FullScreenModal
        className="ai-curriculum-modal"
        open={open}
        onClose={handleClose}
        drawerBgColor={Color.white}
        preventClose={PREVENT_CLOSE_STEPS.has(step)}
      >
        <AILayoutBackground className="ai-curriculum-modal__background">
          <FullScreenModalHeader>Curriculum Assistant</FullScreenModalHeader>
          <FullScreenModalBody>
            <div className="ai-curriculum-modal__body">
              {step === STEPS.ENTRY_POINT && (
                <AiEntryPointStep
                  orgCode={orgCode}
                  onSelect={handleEntrySelected}
                  onError={showError}
                />
              )}

              {step === STEPS.REHYDRATING && (
                <AiLoadingStep
                  label="Picking up where you left off…"
                  subtext="Restoring your Curriculum Assistant session."
                />
              )}

              {step === STEPS.SYLLABUS_UPLOAD && session && (
                <AiSyllabusUploadStep
                  file={syllabusFile}
                  error={syllabusFileError}
                  uploading={uploadingSyllabus}
                  progress={uploadProgress}
                  failure={syllabusUploadFailure}
                  onFilePicked={handleSyllabusFilePicked}
                />
              )}

              {step === STEPS.ANALYZING && session && (
                <AiLoadingStep
                  orgCode={orgCode}
                  sessionId={session.id}
                  targetStatus={SESSION_STATUS.ANALYZED}
                  runKey={pollRunKey}
                  label="Analyzing your syllabus…"
                  subtext="We're extracting learning objectives and standards. This can take a few minutes for larger documents."
                  onComplete={handleAnalyzed}
                  onError={handleAnalysisFailed}
                />
              )}

              {step === STEPS.STATE_SELECT && session && (
                <AiStateSelectStep
                  selectedState={stateSelection.state}
                  selectedSubject={stateSelection.subject}
                  selectedSourceName={stateSelection.sourceName}
                  onSelectedStateChange={(state) =>
                    // Cascading reset — changing the state invalidates both
                    // downstream selections.
                    setStateSelection({
                      state,
                      subject: null,
                      sourceName: null,
                    })
                  }
                  onSelectedSubjectChange={(subject) =>
                    // Changing the subject invalidates the framework drill-down.
                    setStateSelection((prev) => ({
                      ...prev,
                      subject,
                      sourceName: null,
                    }))
                  }
                  onSelectedSourceNameChange={(sourceName) =>
                    setStateSelection((prev) => ({ ...prev, sourceName }))
                  }
                  error={standardsError}
                  onError={setStandardsError}
                />
              )}

              {step === STEPS.LOADING_STANDARDS && session && (
                <AiLoadingStep
                  orgCode={orgCode}
                  sessionId={session.id}
                  targetStatus={SESSION_STATUS.STANDARDS_LOADED}
                  runKey={pollRunKey}
                  label="Loading state standards…"
                  onComplete={handleStandardsLoaded}
                  onError={handleStandardsLoadFailed}
                />
              )}

              {step === STEPS.FORM && session && (
                <AiFormStep
                  entryType={session.entryType}
                  standardsContext={standardsContext}
                  analyzerSummary={analyzerSummary}
                  criteria={criteria}
                  errors={criteriaErrors}
                  error={submitError}
                  onChange={handleCriteriaChange}
                />
              )}

              {step === STEPS.RECOMMENDING && session && (
                <AiLoadingStep
                  orgCode={orgCode}
                  sessionId={session.id}
                  targetStatus={SESSION_STATUS.RECOMMENDED}
                  runKey={pollRunKey}
                  label="Generating recommendations…"
                  subtext="The AI is matching simulations to your standards."
                  onComplete={handleRecommendationsReady}
                  onError={handleRecommendingFailed}
                />
              )}

              {step === STEPS.RECOMMENDATIONS && session && (
                <AiRecommendationsStep
                  orgCode={orgCode}
                  sessionId={session.id}
                  entryType={session.entryType}
                  standardsContext={standardsContext}
                  recommendations={recommendations}
                  onRecommendations={setRecommendations}
                  error={buildError}
                  onError={showError}
                />
              )}

              {step === STEPS.BUILDING && (
                <AiLoadingStep
                  label="Building your course…"
                  subtext="Setting up your new course with the selected content."
                />
              )}

              {step === STEPS.COMPLETE && (
                <AiBuildCompleteStep buildResult={buildResult} />
              )}

              {step === STEPS.ERROR && <AiErrorStep error={errorMessage} />}
            </div>
          </FullScreenModalBody>
          <FullScreenModalFooter>
            <div className="ai-curriculum-modal__footer">
              <div className="ai-curriculum-modal__footer-start">
                {showDownload && (
                  <Button
                    icon={["fa-regular", "file-export"]}
                    loading={downloading}
                    onClick={handleDownloadArtifacts}
                  >
                    Download Artifacts
                  </Button>
                )}
                {!!session?.id && !rehydrating && settingsEditingEnabled && (
                  <Button
                    icon={["fa-regular", "sliders"]}
                    onClick={() => setOverridesOpen(true)}
                  >
                    Advanced Settings
                  </Button>
                )}
                {downloadError && (
                  <span className="ai-curriculum-modal__download-error">
                    {downloadError}
                  </span>
                )}
              </div>
              <div className="ai-curriculum-modal__footer-note">
                {showStepCounter ? (
                  <AiStepCounter
                    current={stepIndex}
                    total={stepCounter.total}
                  />
                ) : (
                  <AiModalFooterNote />
                )}
              </div>
              <div className="ai-curriculum-modal__footer-end">{footer}</div>
            </div>
          </FullScreenModalFooter>
        </AILayoutBackground>
      </FullScreenModal>
      <AiOverridesModal
        enabled={settingsEditingEnabled}
        open={overridesOpen}
        onClose={() => setOverridesOpen(false)}
        settings={settingsCatalog?.settings}
        loading={settingsLoading}
        overrideSettings={overrideSettings}
        taskInstructions={taskInstructions}
        fieldErrors={overrideErrors}
        onSettingChange={handleSettingChange}
        onTaskInstructionsChange={setTaskInstructions}
      />
    </>
  );
};
